import { SelectChangeEvent } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAxios } from "../../utils/endpoints";
import axios from "axios";
import fileDownload from "js-file-download";
import { RefundDetail } from "../../types/RefundDetail";
import { DropdownValue } from "../../types/DropdownValue";
import { useParams, useNavigate } from "react-router-dom";

type IState = {
  refundStatus: number;
  callId: number;
  firstname: string;
  lastname: string;
  phone: string;
  reason: number;
  reviewNotes: string;
  notes: string;
  statusReason: number;
};

type ILinkState = {
  filename: string;
  url: string;
  docDate: string;
};

type ReasonItems = {
  id: number;
  description: string;
  isActive: boolean;
  requiresFile: boolean;
};

const initialState: IState = {
  callId: 0,
  firstname: "",
  lastname: "",
  reviewNotes: "",
  notes: "",
  phone: "",
  statusReason: 0,
  reason: 0,
  refundStatus: 0,
};

type DropdownItems = {
  id: string;
  description: string;
};

const initialSnackbarState = {
  message: "",
  level: "",
  isOpen: false,
};

export const useRefundDetailForm = () => {
  const { refundId } = useParams();
  const navigate = useNavigate();

  const [pageTitle, setPageTitle] = useState<string>("");

  const [state, setState] = useState<IState>(initialState);
  const [link, setLink] = useState<ILinkState>({
    filename: "",
    docDate: "",
    url: "",
  });

  const [statusItems, setStatusItems] = useState<DropdownValue[]>([]);
  const [reasonItems, setReasonItems] = useState<ReasonItems[]>([]);
  const [nonBillReasons, setNonBillReasons] = useState<DropdownItems[]>([]);
  const [rejectReasons, setRejectReasons] = useState<DropdownItems[]>([]);

  const [isOpenViewChangesDialog, setIsOpenViewChangesDialog] =
    useState<boolean>(false);
  const [snackbarState, setSnackbarState] = useState(initialSnackbarState);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const openFile = () => {
    window.open(link.url);
  };

  const onOpenViewChangesDialog = () => setIsOpenViewChangesDialog(true);

  const onCloseSnackbar = () => setSnackbarState(initialSnackbarState);
  const onCloseViewChangesDialog = () => setIsOpenViewChangesDialog(false);

  const onDownload = async (): Promise<void> => {
    await axios
      .get(link.url, { responseType: "blob" })
      .then((res) => {
        fileDownload(res.data, link.filename);
      })
      .catch((error) => {
        console.error("could not download file");
      });
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onChangeSelect = (event: SelectChangeEvent<unknown>) => {
    const { name, value } = event.target;

    setState((prevState) => ({
      ...prevState,
      [name]: value as unknown as string,
    }));
  };

  const onFetchNonBillReason = async (): Promise<void> => {
    try {
      const response = await getAxios("/options/non-bill-reason", "GET");

      const data = await response.data;

      setNonBillReasons(data);
    } catch (error) {
      setSnackbarState({
        message: "Could not get non bill reasons",
        level: "error",
        isOpen: true,
      });
    }
  };

  const onFetchRejectReason = async (): Promise<void> => {
    try {
      const response = await getAxios("/refund/reject-reasons", "GET");

      const data = await response.data;

      setRejectReasons(data);
    } catch (error) {
      setSnackbarState({
        message: "could not get reject reasons",
        level: "error",
        isOpen: true,
      });
    }
  };

  const onFetchStatus = async (): Promise<void> => {
    try {
      const response = await getAxios("/refund/statuses", "GET");

      const data: DropdownValue[] = await response.data;

      setStatusItems(data);
    } catch (error) {
      setSnackbarState({
        message: "could not get statuses",
        level: "error",
        isOpen: true,
      });
    }
  };

  const onFetchReasons = async (): Promise<void> => {
    try {
      const response = await getAxios("/refund/reasons", "GET");

      const data: ReasonItems[] = await response.data;

      setReasonItems(data);
    } catch (error) {
      setSnackbarState({
        message: "could not get refund reasons",
        level: "error",
        isOpen: true,
      });
    }
  };

  const onFetchRefund = async (): Promise<void> => {
    try {
      const response = await getAxios(`/refund/${refundId}`, "GET");

      const data: RefundDetail = await response.data;

      setPageTitle(data.headerText);

      let rval: number = 0;

      if (data.refundStatusId === 2) {
        rval = data.nbrId;
      } else if (data.refundStatusId === 3) {
        rval = data.rejectReasonId;
      }

      setState({
        callId: data.callId,
        firstname: data.callerFirstName,
        lastname: data.callerLastName,
        notes: data.notes,
        reviewNotes: data.reviewNotes,
        phone: data.callerPhone,
        reason: data.refundReasonId,
        refundStatus: data.refundStatusId,
        statusReason: rval,
      });

      setLink(data.links);

      setIsLoading(false);
    } catch (error) {
      setSnackbarState({
        message: "could not get refund data",
        level: "error",
        isOpen: true,
      });
    }
  };

  const onCancel = () => {
    navigate("/view-refunds");
  };

  const onSave = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await getAxios("/refund", "PUT", {
        refundId: refundId,
        statusId: state.refundStatus,
        reasonId: state.refundStatus === 3 ? state.statusReason : 0,
        nonBillReasonId: state.refundStatus === 2 ? state.statusReason : 0,
        rprId: 0,
        reviewNotes: state.reviewNotes,
      });

      setSnackbarState({
        message: "Refund has been updated successfully",
        level: "success",
        isOpen: true,
      });
    } catch (error) {
      setSnackbarState({
        message: "Something went wrong",
        level: "error",
        isOpen: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const statusDropdownLabel = (): string => {
    let label: string = "";

    if (state.refundStatus === 2) {
      label = "Non bill / Accept Reason";
    } else if (state.refundStatus === 3) {
      label = "Reject Reason";
    }

    return label;
  };

  useEffect(() => {
    onFetchStatus();
    onFetchRefund();
    onFetchReasons();
  }, []);

  useEffect(() => {
    if (state.refundStatus === 2) {
      onFetchNonBillReason();
    } else if (state.refundStatus === 3) {
      onFetchRejectReason();
    }
  }, [state.refundStatus]);

  return {
    pageTitle,
    state,
    link,
    isOpenViewChangesDialog,
    reasonItems,
    statusItems,
    isLoading,
    nonBillReasons,
    rejectReasons,
    snackbarState,
    onCloseSnackbar,
    statusDropdownLabel,
    onDownload,
    openFile,
    onChangeSelect,
    onChange,
    onCloseViewChangesDialog,
    onOpenViewChangesDialog,
    onSave,
    onCancel,
    refundId,
  };
};
